import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { getFirebaseAuth } from "@/auth/firebase";

const provider = new GoogleAuthProvider();

export function subscribeAuth(listener: (user: User | null) => void): () => void {
  const auth = getFirebaseAuth();
  if (!auth) {
    listener(null);
    return () => {};
  }
  return onAuthStateChanged(auth, listener);
}

/** Wait until persisted sign-in state is loaded (avoid treating user as signed out too early). */
export async function waitForAuth(): Promise<User | null> {
  const auth = getFirebaseAuth();
  if (!auth) {
    return null;
  }
  await auth.authStateReady();
  return auth.currentUser;
}

export async function signInWithGoogle(): Promise<User> {
  const auth = getFirebaseAuth();
  if (!auth) {
    throw new Error("Firebase ยังไม่ได้ตั้งค่า — ใส่ VITE_FIREBASE_* ใน .env.local");
  }
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export function googleSignInErrorMessage(err: unknown): string {
  const code =
    typeof err === "object" && err !== null && "code" in err
      ? String((err as { code: unknown }).code)
      : "";
  switch (code) {
    case "auth/popup-blocked":
      return "ป๊อปอัปถูกบล็อก — อนุญาตป๊อปอัปของไซต์นี้แล้วกดอีกครั้ง";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "ปิดหน้าต่างก่อนเข้าสู่ระบบ ลองใหม่อีกครั้ง";
    case "auth/unauthorized-domain":
      return "โดเมนนี้ยังไม่ได้อนุญาตใน Firebase Auth";
    default:
      return err instanceof Error ? err.message : "เข้าสู่ระบบไม่สำเร็จ ลองใหม่อีกครั้ง";
  }
}

/** Safe in-app path after Google sign-in (`?next=`), default `/home`. */
export function resolvePostLoginPath(search = window.location.search): string {
  const next = new URLSearchParams(search).get("next");
  if (next && next.startsWith("/") && !next.startsWith("//")) {
    return next;
  }
  return "/home";
}

export async function signOutUser(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) {
    return;
  }
  const uid = auth.currentUser?.uid;
  if (uid) {
    const { flushPlayerCloudPush } = await import("@/cloud/player-cloud-save");
    const { showPlayerCloudSaveToast } = await import("@/cloud/player-cloud-toast");
    const ok = await flushPlayerCloudPush(uid);
    if (!ok) {
      showPlayerCloudSaveToast(
        "บันทึกคลาวด์ไม่ครบ — ยังออกจากระบบได้ แต่เครื่องอื่นอาจไม่เห็นข้อมูลล่าสุด",
      );
    }
  }
  await signOut(auth);
}
